/**
 * MobilePDFPreview Component
 * Canvas-based PDF rendering for mobile devices
 * Uses react-pdf (different from @react-pdf/renderer)
 */

import { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { pdf } from '@react-pdf/renderer';
import { InvoiceDocument } from './PDFPreview';
import type { Invoice } from '@shared/schemas/invoice.schema';
import type { PaperSize } from '@/types/paper';
import type { PageLayout } from '@/utils/pagination';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// Configure worker - using jsDelivr CDN for reliable CORS support
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface MobilePDFPreviewProps {
  invoice: Invoice;
  paperSize: PaperSize;
  pageLayouts: PageLayout[];
}

export function MobilePDFPreview({ invoice, paperSize, pageLayouts }: MobilePDFPreviewProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [containerWidth, setContainerWidth] = useState<number>(Math.min(typeof window !== 'undefined' ? window.innerWidth - 40 : 600, 600));

  // Generate PDF blob and create object URL
  useEffect(() => {
    const generatePDF = async () => {
      try {
        const doc = <InvoiceDocument invoice={invoice} paperSize={paperSize} pageLayouts={pageLayouts} />;
        const blob = await pdf(doc).toBlob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setLoading(false);
      } catch (error) {
        console.error('Failed to generate PDF:', error);
        setLoading(false);
      }
    };

    generatePDF();

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [invoice, paperSize, pageLayouts]);

  // Handle viewport resize for responsive width
  useEffect(() => {
    const handleResize = () => {
      const newWidth = Math.min(window.innerWidth - 40, 600);
      setContainerWidth(newWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Generating PDF...</div>;
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px',
      width: '100%'
    }}>
      <Document
        file={pdfUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={<div style={{ padding: '20px', textAlign: 'center' }}>Loading PDF...</div>}
        error={<div style={{ padding: '20px', textAlign: 'center', color: '#DC2626' }}>Failed to load PDF</div>}
      >
        {Array.from(new Array(numPages), (_, index) => (
          <div key={`page_${index + 1}`} style={{ marginBottom: '20px' }}>
            <Page
              pageNumber={index + 1}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              width={containerWidth}
            />
          </div>
        ))}
      </Document>
    </div>
  );
}
